import { useEffect, useRef, useState } from 'react';
import i18n from '../i18n';

export default function useUndoActions(defaultDelayMs = 10000) {
    const [pendingActions, setPendingActions] = useState([]);
    const [toasts, setToasts] = useState([]);
    const pendingActionMapRef = useRef({});
    const toastTimersRef = useRef({});

    useEffect(() => {
        return () => {
            const currentMap = pendingActionMapRef.current;
            Object.values(currentMap).forEach((x) => clearTimeout(x.timer));
            const toastMap = toastTimersRef.current;
            Object.values(toastMap).forEach((timer) => clearTimeout(timer));
        };
    }, []);

    function pushToast(message, type = 'info', ttlMs = 3500) {
        const id = `${Date.now()}_${Math.random()}`;
        setToasts(prev => [...prev, { id, message, type }]);
        const timer = setTimeout(() => {
            delete toastTimersRef.current[id];
            setToasts(prev => prev.filter(x => x.id !== id));
        }, ttlMs);
        toastTimersRef.current[id] = timer;
    }

    function dismissToast(id) {
        const timer = toastTimersRef.current[id];
        if (timer) {
            clearTimeout(timer);
            delete toastTimersRef.current[id];
        }
        setToasts(prev => prev.filter(x => x.id !== id));
    }

    function scheduleUndoableAction(label, action, delayMs = defaultDelayMs) {
        const id = `${Date.now()}_${Math.random()}`;
        const timer = setTimeout(async () => {
            try {
                await action();
                pushToast(i18n.t('messages:undoExecuted', { label }), 'success');
            } catch {
                pushToast(i18n.t('messages:undoError', { label }), 'error');
            } finally {
                delete pendingActionMapRef.current[id];
                setPendingActions(prev => prev.filter(x => x.id !== id));
            }
        }, delayMs);

        pendingActionMapRef.current[id] = { timer, action };
        setPendingActions(prev => [...prev, { id, label, createdAt: Date.now(), delayMs }]);
        pushToast(i18n.t('messages:undoScheduled', { label }), 'warning');
    }

    function cancelPendingAction(id) {
        const entry = pendingActionMapRef.current[id];
        if (!entry) return;
        clearTimeout(entry.timer);
        delete pendingActionMapRef.current[id];
        const action = pendingActions.find(x => x.id === id);
        setPendingActions(prev => prev.filter(x => x.id !== id));
        pushToast(
            i18n.t('messages:undoCancelled', { label: action?.label || i18n.t('messages:undoActionFallback') }),
            'info'
        );
    }

    async function executePendingActionNow(id) {
        const entry = pendingActionMapRef.current[id];
        if (!entry) return;
        clearTimeout(entry.timer);
        delete pendingActionMapRef.current[id];
        const action = pendingActions.find(x => x.id === id);
        setPendingActions(prev => prev.filter(x => x.id !== id));
        const fallback = i18n.t('messages:undoActionFallback');
        try {
            await entry.action();
            pushToast(i18n.t('messages:undoExecuted', { label: action?.label || fallback }), 'success');
        } catch {
            pushToast(i18n.t('messages:undoError', { label: action?.label || fallback }), 'error');
        }
    }

    return {
        pendingActions,
        toasts,
        scheduleUndoableAction,
        cancelPendingAction,
        executePendingActionNow,
        dismissToast
    };
}
